import { AnimatePresence, motion } from 'motion/react';

import { Icons } from '@/assets/icons/icons';
import { Box, XStack, YStack } from '@/components/ui';
import { getIntegrationIcon } from '@/integrations/integration-icons';
import { cn } from '@/lib/utils';

import { useFeedDemo, type DemoCard } from './feed-demo.hooks';
import { DEMO_SOURCES } from './feed-onboarding.config';
import './feed-onboarding.css';

const EASE = [0.6, 0.6, 0, 1] as const;

const CURVE_BEND = [0.6, 0.4, 0.68];
const PLACEHOLDER_OPACITIES = [1, 0.7, 0.5, 0.35, 0.2];

const SIZES = {
  compact: {
    chipsWidth: 128,
    connectorWidth: 52,
    chipHeight: 40,
    chipGap: 12,
    cardHeight: 44,
    cardGap: 6,
    height: 250,
  },
  regular: {
    chipsWidth: 150,
    connectorWidth: 64,
    chipHeight: 44,
    chipGap: 14,
    cardHeight: 46,
    cardGap: 7,
    height: 300,
  },
};

// Each source's line ends at its first card. Cards stack in source order, so
// that card sits below every card of the sources before it.
const FIRST_CARD_SLOTS = DEMO_SOURCES.map((_, index) =>
  DEMO_SOURCES.slice(0, index).reduce(
    (count, source) => count + source.cards.length,
    0
  )
);

const CHIPS_TOP_PADDING = 14;

const activeBorderClass =
  'border-[hsla(195,60%,80%,1)] dark:border-[hsla(195,50%,35%,1)]';

const DemoCardBody = ({ card }: { card: DemoCard }) => {
  const Icon = getIntegrationIcon(DEMO_SOURCES[card.sourceIndex].toolkitName);

  if (card.isLoading) {
    return (
      <>
        <div className="feed-demo-shimmer size-4 shrink-0 rounded" />
        <YStack className="flex-1 gap-[7px]">
          <div className="feed-demo-shimmer h-[7px] w-[34%] rounded" />
          <div className="feed-demo-shimmer h-[9px] w-[70%] rounded" />
        </YStack>
      </>
    );
  }

  return (
    <>
      <Icon className="size-4 shrink-0" />
      <YStack className="flex-1 gap-[7px]">
        <div
          className="h-[7px] rounded bg-border-strongest"
          style={{ width: card.metaWidth }}
        />
        <div
          className="h-[9px] rounded bg-foreground"
          style={{ width: card.titleWidth }}
        />
      </YStack>
    </>
  );
};

export const FeedDemo = ({
  size,
  height,
}: {
  size: keyof typeof SIZES;
  height?: number;
}) => {
  const { sources, cards } = useFeedDemo();
  const {
    chipsWidth,
    connectorWidth,
    chipHeight,
    chipGap,
    cardHeight,
    cardGap,
  } = SIZES[size];
  const chipCenterY = (index: number) =>
    CHIPS_TOP_PADDING + chipHeight / 2 + index * (chipHeight + chipGap);
  const cardCenterY = (slot: number) =>
    slot * (cardHeight + cardGap) + cardHeight / 2;

  return (
    <motion.div
      aria-hidden
      className="relative grid w-full shrink-0 overflow-hidden text-left"
      style={{
        gridTemplateColumns: `${chipsWidth}px ${connectorWidth}px minmax(0,1fr)`,
      }}
      initial={false}
      animate={{ height: height ?? SIZES[size].height }}
      transition={{ duration: 0.45, ease: EASE }}
    >
      <YStack style={{ gap: chipGap, paddingTop: CHIPS_TOP_PADDING }}>
        {DEMO_SOURCES.map((source, index) => {
          const state = sources[index];
          const Icon = getIntegrationIcon(source.toolkitName);
          return (
            <motion.div
              key={source.toolkitName}
              className={cn(
                'flex items-center gap-2 rounded-[10px] border bg-white pl-[9px] pr-2.5 text-[12.5px] font-semibold transition-shadow duration-300 dark:bg-surface',
                state.isActive || state.isDone
                  ? activeBorderClass
                  : 'border-border',
                state.isActive && 'shadow-card'
              )}
              style={{ height: chipHeight }}
              animate={{
                scale: state.isTapped ? 0.96 : 1,
                y: state.isActive && !state.isTapped ? -2 : 0,
              }}
              transition={{ duration: 0.35, ease: EASE }}
            >
              <Icon className="size-4 shrink-0" />
              <span className="min-w-0 flex-1 truncate">{source.name}</span>
              {state.isDone && (
                <Icons.check className="size-3 shrink-0 text-brand-dark-emerald-green dark:text-brand-emerald-green" />
              )}
            </motion.div>
          );
        })}
      </YStack>

      <svg
        className="pointer-events-none overflow-visible"
        width={connectorWidth}
        height={SIZES[size].height}
      >
        {DEMO_SOURCES.map((source, index) => {
          const state = sources[index];
          const y1 = chipCenterY(index);
          const y2 = cardCenterY(FIRST_CARD_SLOTS[index]);
          const bendX = connectorWidth * CURVE_BEND[index];
          const path = `M0,${y1} C${bendX},${y1} ${bendX},${y2} ${connectorWidth},${y2}`;
          return (
            <g key={source.toolkitName}>
              <path
                d={path}
                fill="none"
                className="stroke-border-strong"
                strokeWidth={1.5}
                strokeDasharray="4 5"
                strokeLinecap="round"
              />
              <motion.path
                d={path}
                fill="none"
                className="stroke-brand-cyan-cornflower-blue"
                strokeWidth={2}
                strokeLinecap="round"
                initial={false}
                animate={{ pathLength: state.isLineDrawn ? 1 : 0 }}
                transition={{ duration: 0.6, ease: EASE }}
              />
              {state.isFlowing && (
                <path
                  d={path}
                  fill="none"
                  stroke="white"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeDasharray="6 26"
                  className="feed-demo-flow opacity-90"
                />
              )}
              <circle
                cx={connectorWidth}
                cy={y2}
                r={3}
                className={cn(
                  'transition-colors duration-500',
                  state.isLineDrawn
                    ? 'fill-brand-cyan-cornflower-blue'
                    : 'fill-border-strong'
                )}
              />
            </g>
          );
        })}
      </svg>

      <div className="relative min-w-0 overflow-hidden">
        <YStack style={{ gap: cardGap }}>
          <AnimatePresence initial={false}>
            {cards.map((card) => (
              <motion.div
                key={card.key}
                className="shrink-0 overflow-hidden"
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: cardHeight, opacity: 1 }}
                exit={{ opacity: 0, transition: { duration: 0.2 } }}
                transition={{ duration: 0.36, ease: EASE }}
              >
                <XStack
                  className="items-center gap-2.5 rounded-[10px] border border-border bg-white px-3 dark:bg-surface"
                  style={{ height: cardHeight }}
                >
                  <DemoCardBody card={card} />
                </XStack>
              </motion.div>
            ))}
          </AnimatePresence>
          {PLACEHOLDER_OPACITIES.map((opacity) => (
            <Box
              key={opacity}
              className="shrink-0 rounded-[10px] border-[1.5px] border-dashed border-border-strong"
              style={{ height: cardHeight, opacity }}
            />
          ))}
        </YStack>
      </div>

      <Box className="pointer-events-none absolute inset-x-0 bottom-0 h-[70px] bg-gradient-to-b from-transparent to-background md:h-[90px]" />
    </motion.div>
  );
};
